import { faEllipsisVertical, faEyeSlash, faFile, faGrip, faHouseBlank, faLink, faListDropdown, IconDefinition } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { INavigationDropdownOption, INavigationDropdownOptionSections, NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { cloneDeep } from "lodash";
import { IWebsiteBuilderRoot } from "pages/secure/facility/websiteBuilder/editor/nodes/root/websiteBuilderRoot";
import { websiteBuilderAddLinkProtocol } from "pages/secure/facility/websiteBuilder/editor/utility/websiteBuilderAddLinkProtocol";
import { websiteBuilderSlugify } from "pages/secure/facility/websiteBuilder/editor/utility/websiteBuilderSlugify";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { websiteBuilderAddNavigationMenuItem, websiteBuilderAddPage, websiteBuilderDeleteNavigationMenuItem, websiteBuilderDeletePage, websiteBuilderSelectPage, websiteBuilderSetHomePage, websiteBuilderUpdateNavigationMenuItem } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { IWebsiteBuilderWebsiteVersionNavigationMenuExternalItem, IWebsiteBuilderWebsiteVersionNavigationMenuPageItem, TWebsiteBuilderWebsiteVersionNavigationMenuItem, TWebsiteBuilderWebsiteVersionPage } from "redux/reducers/models/websiteBuilder";
import { TManagePagesViewSubView } from "./ManagePagesView";
import "./navigationMenuItem.scss";
import { useDrag } from "react-dnd";
import { TWebsiteBuilderNavigationMenuItemsDomNodeRefMap, WEBSITE_BUILDER_NAVIGATION_MENU_ITEMS_DROP_ITEM } from "./NavigationMenuItems";
import { websiteBuilderSanitize } from "pages/secure/facility/websiteBuilder/editor/sanitization/websiteBuilderSanitize";

interface INavigationMenuItemProps {
  item: TWebsiteBuilderWebsiteVersionNavigationMenuItem;
  setSubView: React.Dispatch<React.SetStateAction<TManagePagesViewSubView>>;
  rootDomNodeRefMap?: TWebsiteBuilderNavigationMenuItemsDomNodeRefMap;
  dropdownDomNodeRefMap?: TWebsiteBuilderNavigationMenuItemsDomNodeRefMap;
  disabled: boolean;
}

interface INavigationMenuDragItemState {
  isDragging: boolean;
}

export function NavigationMenuItem(props: INavigationMenuItemProps) {
  const { item, setSubView, rootDomNodeRefMap, dropdownDomNodeRefMap, disabled } = props;

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const websiteVersion = useAppSelector(store => store.websiteBuilderStore.editor.websiteVersion);
  const pages = useAppSelector(store => store.websiteBuilderStore.editor.pages);
  const pageMap = useAppSelector(store => store.websiteBuilderStore.editor.pageMap);
  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const pagesAutoSaveStatus = useAppSelector(store => store.websiteBuilderStore.editor.autoSaveStatusState.pages);
  const navigationMenuItems = useAppSelector(store => store.websiteBuilderStore.editor.navigationMenuItems);

  const pageItem = item.type === "page";
  const externalItem = item.type === "external";
  const dropdownItem = item.type === "dropdown";

  const errorPageItem = pageItem && pagesAutoSaveStatus[item.page_id] === "error";
  const activePageItem = pageItem && item.page_id === activePageId;

  const page: TWebsiteBuilderWebsiteVersionPage = useMemo(() => {
    if (item.type !== "page" || item.page_id == null) {
      return null;
    }

    return pageMap.get(item.page_id);
  }, [item, pageMap]);

  const homePageItem = pageItem && page?.is_home;

  const [dragState, drag, dragPreview] = useDrag<TWebsiteBuilderWebsiteVersionNavigationMenuItem, unknown, INavigationMenuDragItemState>(() => {
    return {
      item: cloneDeep(item),
      type: WEBSITE_BUILDER_NAVIGATION_MENU_ITEMS_DROP_ITEM,
      canDrag: !disabled,
      collect: (monitor): INavigationMenuDragItemState => {
        return {
          isDragging: !!monitor.isDragging(),
        };
      },
    };
  }, [item, disabled]);

  function toggleNavigationMenuItemHidden(item: TWebsiteBuilderWebsiteVersionNavigationMenuItem) {
    if (item.type === "page") {
      void dispatch(websiteBuilderUpdateNavigationMenuItem({
        id: item.id,
        page_id: item.page_id,
        type: item.type,
        label: null,
        external_url: null,
        open_in_new_tab: false,
        position: item.position,
        parent_id: item.parent_id,
        hidden: !item.hidden,
      }, navigationMenuItems, t));
    } else if (item.type === "external") {
      void dispatch(websiteBuilderUpdateNavigationMenuItem({
        id: item.id,
        page_id: null,
        type: item.type,
        label: item.label,
        external_url: item.external_url,
        open_in_new_tab: item.open_in_new_tab,
        position: item.position,
        parent_id: item.parent_id,
        hidden: !item.hidden,
      }, navigationMenuItems, t));
    } else if (item.type === "dropdown") {
      void dispatch(websiteBuilderUpdateNavigationMenuItem({
        id: item.id,
        page_id: null,
        type: item.type,
        label: item.label,
        external_url: null,
        open_in_new_tab: false,
        position: item.position,
        parent_id: null,
        hidden: !item.hidden,
      }, navigationMenuItems, t));
    }
  }

  function duplicatePage(item: IWebsiteBuilderWebsiteVersionNavigationMenuPageItem, page: TWebsiteBuilderWebsiteVersionPage) {
    let title = page.title;
    let slug = page.slug;
  
    while (pages.some(page => page.title === title || page.slug === slug)) {
      title += ` - ${t("Duplicate")}`;
      slug = websiteBuilderSlugify(title);
    }

    const duplicatedRoot: IWebsiteBuilderRoot = websiteBuilderSanitize(page.root);

    void dispatch(websiteBuilderAddPage(
      page.website_version_id,
      title,
      slug,
      duplicatedRoot,
      item.parent_id,
      t,
    ));
  }

  function deletePage(id: number) {
    void dispatch(websiteBuilderDeletePage(id, t));
  }

  function setHomePage(id: number) {
    void dispatch(websiteBuilderSetHomePage(id, t));
  }

  function duplicateExternalItem(item: IWebsiteBuilderWebsiteVersionNavigationMenuExternalItem) {
    void dispatch(websiteBuilderAddNavigationMenuItem(
      {
        website_version_id: websiteVersion.id,
        page_id: null,
        type: "external",
        label: item.label,
        external_url: item.external_url,
        open_in_new_tab: item.open_in_new_tab,
        parent_id: item.parent_id,
      },
      t,
    ));
  }

  function deleteExternalItem(id: number) {
    void dispatch(websiteBuilderDeleteNavigationMenuItem(id, t));
  }

  function deleteDropdown(id: number) {
    void dispatch(websiteBuilderDeleteNavigationMenuItem(id, t));
  }

  function handleItemClick(clickedItem: TWebsiteBuilderWebsiteVersionNavigationMenuItem) {
    if (clickedItem.type === "page" && clickedItem.page_id != null && pageMap.get(clickedItem.page_id) != null) {
      dispatch(websiteBuilderSelectPage({ activePageId: clickedItem.page_id }));
    } else if (clickedItem.type === "external" && clickedItem.external_url?.length > 0) {
      window.open(websiteBuilderAddLinkProtocol(clickedItem.external_url), "_blank");
    }
  }

  function renderItemIcon() {
    let icon: IconDefinition = null;
    
    if (pageItem) {
      icon = page?.is_home ? faHouseBlank : faFile;
    } else if (externalItem) {
      icon = faLink;
    } else if (dropdownItem) {
      icon = faListDropdown;
    }

    return icon != null ? (
      <FontAwesomeIcon
        className="navigation-menu-item-icon"
        icon={icon}
      />
    ): null;
  }

  function getHiddenOption(item: TWebsiteBuilderWebsiteVersionNavigationMenuItem): INavigationDropdownOption {
    return {
      label: item.hidden ? t("Reveal") : t("Hide"),
      icon: item.hidden ? "eye" : "eye-slash",
      onClick: () => toggleNavigationMenuItemHidden(item),
      disabled,
    };
  }

  function getOptionSections(): INavigationDropdownOptionSections {
    if (pageItem && page != null) {
      const deleteDisabled = disabled || homePageItem;

      return [
        [
          getHiddenOption(item),
          {
            label: t("Rename"),
            icon: "pen",
            onClick: () => setSubView({ type: "rename-page", page: cloneDeep(page) }),
            disabled,
          },
          {
            label: t("Duplicate"),
            icon: "copy",
            onClick: () => duplicatePage(item, page),
            disabled,
          },
          {
            label: t("Set as homepage"),
            icon: "house-blank",
            onClick: () => setHomePage(page.id),
            disabled: disabled || homePageItem,
          },
          {
            label: t("Delete"),
            icon: "trash",
            onClick: () => deletePage(page.id),
            disabled: deleteDisabled,
            color: !deleteDisabled ? "red" : null,
          },
        ],
      ];
    } else if (externalItem) {
      const deleteDisabled = disabled;

      return [
        [
          getHiddenOption(item),
          {
            label: t("Settings"),
            icon: "pen",
            onClick: () => setSubView({ type: "update-link", item: cloneDeep(item) }),
            disabled,
          },
          {
            label: t("Duplicate"),
            icon: "copy",
            onClick: () => duplicateExternalItem(item),
            disabled,
          },
          {
            label: t("Delete"),
            icon: "trash",
            onClick: () => deleteExternalItem(item.id),
            disabled: deleteDisabled,
            color: !deleteDisabled ? "red" : null,
          },
        ],
      ];
    } else if (dropdownItem) {
      const deleteDisabled = disabled;

      return [
        [
          getHiddenOption(item),
          {
            label: t("Rename"),
            icon: "pen",
            onClick: () => setSubView({ type: "rename-dropdown", item: cloneDeep(item) }),
            disabled,
          },
          {
            label: t("Delete"),
            icon: "trash",
            onClick: () => deleteDropdown(item.id),
            disabled: deleteDisabled,
            color: !deleteDisabled ? "red" : null,
          },
        ],
      ];
    } else {
      return [];
    }
  }

  const navigationMenuItemDomNodeRefMapRegistration = useCallback(
    (el: HTMLDivElement) => {
      if (el != null) {
        rootDomNodeRefMap?.current.set(item.id, el);
        dropdownDomNodeRefMap?.current.set(item.id, el);
      } else {
        rootDomNodeRefMap?.current.delete(item.id);
        dropdownDomNodeRefMap?.current.delete(item.id);
      }
    },
    [item.id],
  );
  
  return (
    <div
      ref={el => {
        dragPreview(el);
        navigationMenuItemDomNodeRefMapRegistration(el);
      }}
      className={classNames("website-builder-navigation-menu-item", {
        "navigation-menu-item-disabled": disabled,
        "navigation-menu-item-error-page": errorPageItem,
        "navigation-menu-item-active-page": activePageItem,
        "navigation-menu-item-external-highlight": externalItem,
      })}
      style={{
        visibility: dragState.isDragging ? "hidden" : null,
      }}
      onClick={() => handleItemClick(item)}
    >
      <div ref={drag} className="navigation-menu-item-grip-container">
        <FontAwesomeIcon className="navigation-menu-item-grip" icon={faGrip} />
      </div>
      {renderItemIcon()}
      <div className="navigation-menu-item-title">
        {item.type === "page" ? (page?.title ?? "") : item.label}
      </div>
      {item.hidden && <FontAwesomeIcon className="navigation-menu-item-icon" icon={faEyeSlash} />}
      <div
        className="navigation-menu-item-options"
        onClick={e => e.stopPropagation()}
      >
        <NavigationDropdownNew
          hideChevron
          showPlainTextLabel
          label={
            <FontAwesomeIcon icon={faEllipsisVertical} />
          }
          rightAlign
          sections={getOptionSections()}
        />
      </div>
    </div>
  );
}